import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

export const buttonVariants = cva(
  "text-on-primary focus-visible:outline-primary-focused! disabled:bg-surface-disabled disabled:text-on-surface-disabled flex h-10 items-center justify-center gap-2 rounded-3xl px-4 py-[9.5px] text-sm font-medium transition-all focus-visible:outline-offset-2! enabled:hover:cursor-pointer",
  {
    variants: {
      variant: {
        primary: "bg-primary hover:bg-primary-hovered focus-visible:bg-primary-focused active:bg-primary-pressed",
        danger: "bg-critical hover:bg-critical-hovered focus-visible:bg-critical-focused active:bg-critical-pressed",
        secondary:
          "bg-surface text-on-surface hover:bg-surface-hovered focus-visible:bg-surface-focused active:bg-surface-pressed border px-5.75 focus-visible:outline-offset-[3px]",
        tertiary: "text-primary hover:bg-surface-hovered focus-visible:bg-surface-focused active:bg-surface-pressed",
        circle: "text-on-surface-subdued hover:bg-surface-hovered h-14 w-14 rounded-full bg-transparent px-2",
        unstyle: "",
      },
      disable: {
        true: "disabled:cursor-not-allowed",
      },
      isLoading: {
        true: "bg-surface-disabled pointer-events-none",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  "data-testid"?: string | null
  ref?: React.Ref<HTMLButtonElement>
}

export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"]
type AllowedWithLoadingVariant = "primary" | "secondary" | "tertiary" | "circle"
export type ButtonVariantProps =
  | { variant?: AllowedWithLoadingVariant; isLoading?: boolean }
  | { variant: Exclude<ButtonVariant, AllowedWithLoadingVariant>; isLoading?: never }

const LoadingSpinner = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="animate-spin" width="24" height="24">
      <path
        className="fill-primary"
        d="M19 12c0-3.976-3.024-7-7-7V3a9 9 0 0 1 9 9h-2ZM12 19c3.976 0 7-3.024 7-7h2a9 9 0 0 1-9 9v-2ZM5 12c0 3.976 3.024 7 7 7v2a9 9 0 0 1-9-9h2Z"
      />
    </svg>
  )
}

export const Button = (props: ButtonProps & ButtonVariantProps) => {
  const { className, variant, asChild, isLoading, disabled, children, id, ref, ...rest } = props
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      id={id || props["data-testid"] || ""}
      ref={ref}
      disabled={disabled}
      className={cn(buttonVariants({ variant, className, disable: disabled, isLoading }))}
      {...rest}>
      {isLoading ? <LoadingSpinner /> : children}
    </Comp>
  )
}
